import React, { useState } from 'react';
import { X, Cpu, Save, Zap, Weight } from 'lucide-react';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';

interface DeviceEditModalProps {
  device: any;
  datacenterCode?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const DeviceEditModal: React.FC<DeviceEditModalProps> = ({
  device,
  datacenterCode = '',
  onClose,
  onSuccess
}) => {
  const [name, setName] = useState(device?.name || '');
  const [equipmentType, setEquipmentType] = useState(device?.equipment_type || 'server');
  const [uHeight, setUHeight] = useState<number>(Number(device?.u_height ?? 1));
  const [uPosition, setUPosition] = useState<number>(Number(device?.u_position ?? 1));
  const [mountingPosition, setMountingPosition] = useState(device?.mounting_position || 'Front');
  const [powerW, setPowerW] = useState<number | null>(device?.power_w ?? null);
  const [weightKg, setWeightKg] = useState<number | null>(device?.weight_kg ?? null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const rackId = device.rack_id;
      const endpoint = rackId
        ? `/api/datacenters/${datacenterCode}/racks/${rackId}/equipment/${device.id}`
        : `/api/equipment/${device.id}`;

      await api.put(endpoint, {
        name,
        equipment_type: equipmentType,
        u_height: Number(uHeight),
        u_position: Number(uPosition),
        mounting_position: mountingPosition,
        power_w: powerW,
        weight_kg: weightKg
      });

      toast.success('Характеристики оборудования обновлены');
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Не удалось обновить устройство');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400">
              <Cpu className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-100">Редактирование прибора</h2>
              <p className="text-xs text-slate-400 font-mono">ID: {device.id}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          <div>
            <label htmlFor="dev-edit-name" className="block text-xs font-mono text-slate-400 mb-1">Название прибора</label>
            <input 
              id="dev-edit-name"
              name="name"
              type="text" 
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-500 transition-colors"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="dev-edit-type" className="block text-xs font-mono text-slate-400 mb-1">Тип оборудования</label>
              <select
                id="dev-edit-type"
                name="equipmentType"
                value={equipmentType}
                onChange={(e) => setEquipmentType(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-500 transition-colors"
              >
                <option value="server">Сервер</option>
                <option value="switch">Коммутатор</option>
                <option value="router">Маршрутизатор</option>
                <option value="patch_panel">Патч-панель</option>
                <option value="pdu">PDU (Питание)</option>
                <option value="ups">ИБП (UPS)</option>
              </select>
            </div>

            <div>
              <label htmlFor="dev-edit-mount" className="block text-xs font-mono text-slate-400 mb-1">Тип монтажа</label>
              <select
                id="dev-edit-mount"
                name="mountingPosition"
                value={mountingPosition}
                onChange={(e) => setMountingPosition(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-500 transition-colors"
              >
                <option value="Front">Фронтальный (Front)</option>
                <option value="Rear">Задний (Rear)</option>
                <option value="Full Depth">На всю глубину</option>
                <option value="Left">Левый карман (Zero-U)</option>
                <option value="Right">Правый карман (Zero-U)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="dev-edit-upos" className="block text-xs font-mono text-slate-400 mb-1">Позиция U-слота</label>
              <input 
                id="dev-edit-upos"
                name="uPosition"
                type="number"
                min={device?.is_zero_u ? 0 : 1}
                value={uPosition}
                onChange={(e) => setUPosition(Number(e.target.value))}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-500 transition-colors"
                required
              />
            </div>

            <div>
              <label htmlFor="dev-edit-uheight" className="block text-xs font-mono text-slate-400 mb-1">Высота (U-юниты)</label>
              <input 
                id="dev-edit-uheight"
                name="uHeight"
                type="number"
                min={device?.is_zero_u ? 0 : 1}
                value={uHeight}
                onChange={(e) => setUHeight(Number(e.target.value))}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-500 transition-colors"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="dev-edit-power" className="block text-xs font-mono text-slate-400 mb-1 flex items-center gap-1">
                <Zap className="w-3.5 h-3.5 text-amber-400" /> Мощность (Вт)
              </label>
              <input 
                id="dev-edit-power"
                name="powerW"
                type="number"
                min={0}
                value={powerW ?? ''}
                placeholder="Неизвестно"
                onChange={(e) => setPowerW(e.target.value === '' ? null : e.target.valueAsNumber)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-500 transition-colors"
              />
            </div>

            <div>
              <label htmlFor="dev-edit-weight" className="block text-xs font-mono text-slate-400 mb-1 flex items-center gap-1">
                <Weight className="w-3.5 h-3.5 text-cyan-400" /> Масса (кг)
              </label>
              <input 
                id="dev-edit-weight"
                name="weightKg"
                type="number"
                min={0}
                step={0.5}
                value={weightKg ?? ''}
                placeholder="Неизвестно"
                onChange={(e) => setWeightKg(e.target.value === '' ? null : e.target.valueAsNumber)}
                className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-500 transition-colors"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="pt-4 border-t border-slate-800 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-mono text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-xs font-mono font-medium text-white bg-cyan-600 hover:bg-cyan-500 rounded-lg transition-colors shadow-lg shadow-cyan-950/50 disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Сохранение...' : 'Сохранить изменения'}
            </button>
          </div>

        </form>

      </div>
    </div>
  );
};
